import { Typography } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Card from "@material-ui/core/Card";
import AutocompleteArrayInputEx from "components/_extensions/react-admin/ra-ui-materialui/src/input/AutocompleteArrayInputEx";
import { CoolerTypeSelect } from "components/inputs/CoolerTypeSelect";
import { NumberWithSuffixInput } from "components/inputs/NumberWithSuffixInput";
import {
	CustomerCCTOCDChangeType,
	CustomerCDStorageChargeType,
	CustomerServiceChargeType,
} from "entities/CustomerCDChargeType";
import { MapperType } from "entities/MapperType";
import { PalletReservationType } from "entities/PalletReservationType";
import { SplitOrdersType } from "entities/SplitOrdersType";
import React, { useEffect } from "react";
import {
	BooleanInput,
	ReferenceArrayInput,
	ReferenceInput,
	SelectInput,
	TextInput,
	maxLength,
	minLength,
	required,
	usePermissions,
	useTranslate,
} from "react-admin";
import { useForm, useFormState } from "react-final-form";
import { translationsResource } from "resources/translations";
import { validateLettersOnly } from "utils/validation/general";

import { FormGroupGrid, GridForm, GridInput } from "../../components/GridForm";
import { EmailInput, NumberInputEx, YesNoInput } from "../../components/inputs/";
import { CommodityInput } from "../../components/inputs/CommodityInput";
import LanguageSelect from "../../components/inputs/LanguageSelect";
import { SelectEnumInput } from "../../components/inputs/SelectEnumInput";
import { AppManagerRoles, AppRoles, OrderService, OrderServices } from "../../entities";
import { CompanyCustomerType } from "../../entities";
import { Currencies } from "../../entities/Currencies";
import { isInArray } from "../../utils/general";
import { CardBlock } from "./CardBlock";
import { UpdateForm } from "./UpdateForm";

const ColdStorageSettings = ({ translate, isAppManager }) => {
	return (
		<FormGroupGrid
			sm={12}
			title={translate("resources.customer-companies.settings.title.cold_storage_service")}
		>
			<GridInput
				sm={3}
				component={SelectEnumInput}
				enumObj={CustomerServiceChargeType}
				enumName="cs_service_charge_type"
				source="cs_reefer_service_charge_type"
				key="cs_reefer_service_charge_type"
				label={translate("resources.customer-companies.fields.cs_reefer_service_charge_type")}
				disabled={!isAppManager}
			/>
			<GridInput
				sm={3}
				component={SelectEnumInput}
				enumObj={CustomerServiceChargeType}
				enumName="cs_service_charge_type"
				source="cs_reefer_weekly_charge_type"
				key="cs_reefer_weekly_charge_type"
				label={translate("resources.customer-companies.fields.cs_reefer_weekly_charge_type")}
				disabled={!isAppManager}
			/>
			<GridInput
				sm={3}
				component={SelectEnumInput}
				enumObj={CustomerServiceChargeType}
				enumName="cs_service_charge_type"
				source="cs_reefer_energy_surcharge_type"
				key="cs_reefer_energy_surcharge_type"
				label={translate("resources.customer-companies.fields.cs_reefer_energy_surcharge_type")}
				disabled={!isAppManager}
			/>
			<GridInput
				sm={3}
				component={TextInput}
				source="ceel_prefix_for_reefer"
				key="cell_prefix_for_reefer"
				validate={[minLength(2), maxLength(2), validateLettersOnly]}
				parse={(value) => value.toUpperCase()}
			/>
			<GridInput
				sm={3}
				component={SelectEnumInput}
				enumObj={CustomerServiceChargeType}
				enumName="cs_service_charge_type"
				source="cs_truck_service_charge_type"
				key="cs_truck_service_charge_type"
				label={translate("resources.customer-companies.fields.cs_truck_service_charge_type")}
				disabled={!isAppManager}
			/>
			<GridInput
				sm={3}
				component={SelectEnumInput}
				enumObj={CustomerServiceChargeType}
				enumName="cs_service_charge_type"
				source="cs_truck_weekly_charge_type"
				key="cs_truck_weekly_charge_type"
				label={translate("resources.customer-companies.fields.cs_truck_weekly_charge_type")}
				disabled={!isAppManager}
			/>
			<GridInput
				sm={3}
				component={SelectEnumInput}
				enumObj={CustomerServiceChargeType}
				enumName="cs_service_charge_type"
				source="cs_truck_energy_surcharge_type"
				key="cs_truck_energy_surcharge_type"
				label={translate("resources.customer-companies.fields.cs_truck_energy_surcharge_type")}
				disabled={!isAppManager}
			/>
			<GridInput
				sm={3}
				component={TextInput}
				source="ceel_prefix_for_pallets"
				key="cell_prefix_for_pallets"
				validate={[minLength(2), maxLength(2), validateLettersOnly]}
				parse={(value) => value.toUpperCase()}
			/>
			<GridInput
				sm={6}
				component={SelectEnumInput}
				enumObj={PalletReservationType}
				enumName="cold_storage_order_picking_logic"
				source="cold_storage_order_picking_logic"
				key="cold_storage_order_picking_logic"
			/>
			<GridInput sm={6} component={YesNoInput} source="cold_storage_is_reefer" />
			<GridInput sm={6} component={YesNoInput} source="cold_storage_is_photo_of_label" />
			<GridInput sm={6} key="cold_storage_temperature_type">
				<CoolerTypeSelect source="cold_storage_temperature_type" />
			</GridInput>
			<GridInput
				sm={6}
				component={YesNoInput}
				source="cold_storage_is_t1"
				key="cold_storage_is_t1"
				defaultValue={false}
			/>
			<Typography variant="h6" color="textSecondary" style={{ marginLeft: 12 }}>
				{translate("resources.customer-companies.settings.weight_notice")}
			</Typography>
		</FormGroupGrid>
	);
};

const CompanySettingsForm = (props) => {
	const { permissions } = usePermissions();
	const { values } = useFormState();
	const translate = useTranslate();

	const filter = props.currentCompanyId ? { "id:ne": props.currentCompanyId } : {};
	const isAppManager = permissions && isInArray(AppRoles, permissions.role);
	const isTransporter = values["type"] == CompanyCustomerType.transporter;

	return (
		<GridForm {...props}>
			<GridInput md={3} sm={6} component={LanguageSelect} />
			<GridInput
				lg={1}
				md={2}
				sm={6}
				component={SelectEnumInput}
				enumObj={Currencies}
				translate={false}
				enumName="currency"
				source="currency"
				disabled={!isAppManager}
			/>
			<GridInput md={3} sm={6} component={EmailInput} source="deviation_notification_email" />
			{!isTransporter && (
				<GridInput md={1} sm={6} component={YesNoInput} source="seal" defaultValue={false} />
			)}
			{!isTransporter && (
				<GridInput md={1} sm={6} component={YesNoInput} source="plumb" defaultValue={false} />
			)}
			{!isTransporter && (
				<GridInput md={3} sm={6} component={CommodityInput} source="commodity_id" />
			)}
			<GridInput md={4} sm={6} options={{ size: "small" }}>
				<ReferenceArrayInput
					source="preferred_transporters"
					reference="transporters"
					filter={{ ...filter, no_range: true }}
					sort={{ field: "full_name", order: "ASC" }}
				>
					{/* Choices are shaking, the solution was taken from link below */}
					{/* https://github.com/marmelab/react-admin/issues/5926 */}
					<AutocompleteArrayInputEx optionText="full_name" />
				</ReferenceArrayInput>
			</GridInput>
			{!isTransporter && (
				<GridInput md={1} sm={6}>
					<ReferenceInput source="terminal_id" reference="terminal">
						<SelectInput optionText="name" />
					</ReferenceInput>
				</GridInput>
			)}
			{!isTransporter && (
				<GridInput
					md={1}
					sm={6}
					component={YesNoInput}
					source="is_output_document"
					defaultValue={false}
				/>
			)}
			<GridInput md={1} sm={6}>
				<ReferenceInput
					fullWidth
					reference="mappers"
					source="order_import_mapper_id"
					label={translate("osfc.dialogs.orders-import-upload.fields.mapper_id")}
					filter={{
						type: MapperType.order_import,
						company_id: props.currentCompanyId,
						is_upload_order: true,
					}}
					allowEmpty
				>
					<SelectInput name="name" />
				</ReferenceInput>
			</GridInput>
			<GridInput md={2} sm={6} component={NumberInputEx} source="expiration_warning_days" min={1} />
			<GridInput md={3} sm={6}>
				<BooleanInput label="Copy docs" source="copy_docs_on_clone" />
			</GridInput>
		</GridForm>
	);
};

const CompanySettingsBlockBase = ({ component: Component, ...props }) => {
	const { permissions } = usePermissions();
	const translate = useTranslate();

	const isAdminAppManager = permissions && isInArray(AppRoles, permissions.role);

	const currentCompanyId = permissions?.company_id || (props?.record && props.record.id);

	return currentCompanyId ? (
		<UpdateForm
			id={currentCompanyId}
			resource="customer-companies"
			render={({ body, buttons }) => (
				<CardBlock
					Component={isAdminAppManager ? Box : Card}
					title={isAdminAppManager ? "" : translate("tab.company_settings.name")}
					buttons={buttons}
					children={body}
				/>
			)}
		>
			<Component currentCompanyId={currentCompanyId} />
		</UpdateForm>
	) : (
		<></>
	);
};

const CompanyServiceSettingsForm = (props) => {
	const { permissions } = usePermissions();
	const { values } = useFormState();
	const form = useForm();
	const t = useTranslate();

	const isAppManager = permissions && isInArray(AppManagerRoles, permissions.role);

	useEffect(() => {
		if (values["cd_storage_charge_type"] !== CustomerCDStorageChargeType.hours) {
			form.change("cd_storage_charge_hours", 0);
		}
		if (values["cc_to_cd_change_type"] !== CustomerCCTOCDChangeType.minutes) {
			form.change("cc_to_cd_minutes", 30);
		}
	}, [values["cd_storage_charge_type"], values["cc_to_cd_change_type"]]);

	return (
		<GridForm {...props}>
			<FormGroupGrid
				sm={12}
				title={t("resources.customer-companies.settings.title.cross_doc_service")}
			>
				{(isAppManager ||
					(!isAppManager && values["cc_to_cd_change_type"] !== CustomerCCTOCDChangeType.none)) && (
					<GridInput
						xs={12}
						sm={values["cc_to_cd_change_type"] == CustomerCCTOCDChangeType.minutes ? 6 : 12}
						source="cc_to_cd_change_type"
						component={SelectEnumInput}
						disabled={!isAppManager}
						enumObj={CustomerCCTOCDChangeType}
						enumName="customer_cc_to_cd_change"
						validate={[required()]}
						label={t("resources.customer-companies.fields.cc_to_cd_change_type", {
							cc_service_name: OrderServices[OrderService.reloadCarCar].full_name,
							cd_service_name: OrderServices[OrderService.crossDock].full_name,
						})}
					/>
				)}
				{(isAppManager ||
					(!isAppManager && values["cc_to_cd_change_type"] !== CustomerCCTOCDChangeType.none)) &&
					values["cc_to_cd_change_type"] == CustomerCCTOCDChangeType.minutes && (
						<GridInput
							xs={12}
							sm={6}
							source="cc_to_cd_minutes"
							component={NumberWithSuffixInput}
							disabled={!isAppManager}
							suffix="minutes"
							min={0}
							validate={[required()]}
						/>
					)}
				<GridInput
					xs={12}
					source="cd_service_charge_type"
					component={SelectEnumInput}
					disabled={!isAppManager}
					enumObj={CustomerServiceChargeType}
					enumName="customer_cd_service_charge"
					validate={[required()]}
					label={t("resources.customer-companies.fields.cd_service_charge_type", {
						cd_service_name: OrderServices[OrderService.crossDock].full_name,
					})}
				/>
				<GridInput
					xs={12}
					sm={values["cd_storage_charge_type"] == CustomerCDStorageChargeType.hours ? 6 : 12}
					source="cd_storage_charge_type"
					component={SelectEnumInput}
					disabled={!isAppManager}
					enumObj={CustomerCDStorageChargeType}
					enumName="customer_cd_storage_charge"
					validate={[required()]}
					label={t("resources.customer-companies.fields.cd_storage_charge_type", {
						cd_service_name: OrderServices[OrderService.crossDock].full_name,
					})}
				/>
				{values["cd_storage_charge_type"] == CustomerCDStorageChargeType.hours && (
					<GridInput
						xs={12}
						sm={6}
						source="cd_storage_charge_hours"
						component={NumberWithSuffixInput}
						disabled={!isAppManager}
						suffix="hours"
						min={0}
						validate={[required()]}
					/>
				)}
			</FormGroupGrid>
			<ColdStorageSettings translate={t} isAppManager={isAppManager} />
			<FormGroupGrid
				sm={12}
				title={t("resources.customer-companies.settings.title.common_service_settings")}
			>
				<GridInput
					component={YesNoInput}
					source="incoming_order_can_scan_to_create"
					key="incoming_order_can_scan_to_create"
					defaultValue={false}
					helperText={t(
						"resources.customer-companies.fields.helper_text.incoming_order_can_scan_to_create"
					)}
				/>
			</FormGroupGrid>
		</GridForm>
	);
};

export const CompanySettingsBlock = (props) => (
	<CompanySettingsBlockBase component={CompanySettingsForm} {...props} />
);
export const CompanyServiceSettingsBlock = (props) => (
	<CompanySettingsBlockBase component={CompanyServiceSettingsForm} {...props} />
);
